/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  Flex,
  HStack,
  Input,
  InputProps,
  Text,
  useNumberInput,
} from '@chakra-ui/react';
import IconTicket from 'components/IconTicket/IconTicket';
import { useLoadingTrailer } from 'context';
import useDebounce from 'hooks/useDebounce';
import { useCallback, useState } from 'react';

interface TicketAmountProps extends InputProps {
  value: number;
  setValue: (value: number) => void;
}

const TicketAmount = ({ value, setValue, ...rest }: TicketAmountProps) => {
  const [displayValue, setDisplayValue] = useState(value);
  const { isLoadingTickets, setIsLoadingTickets } = useLoadingTrailer();

  const debouncedSetValue = useCallback(
    useDebounce((newValue: number) => {
      setIsLoadingTickets(true);
      setValue(newValue);
      setIsLoadingTickets(false);
    }, 1000),
    [setValue, setIsLoadingTickets]
  );

  const handleChange = useCallback(
    (valueAsString: string) => {
      const newValue = parseInt(valueAsString, 10);
      if (isNaN(newValue)) {
        return;
      }
      setDisplayValue(newValue);
      debouncedSetValue(newValue);
    },
    [debouncedSetValue]
  );

  const { getInputProps, getIncrementButtonProps, getDecrementButtonProps } =
    useNumberInput({
      step: 1,
      defaultValue: 1,
      min: 1,
      max: 100,
      value: displayValue,
      onChange: handleChange,
      isDisabled: isLoadingTickets,
    });

  const inc = getIncrementButtonProps();
  const dec = getDecrementButtonProps();
  const input = getInputProps();

  return (
    <Flex
      background="linear-gradient(90deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0) 100%)"
      borderRadius="0.25rem"
      p={{ base: '0.5rem', md: '1rem' }}
      position="relative"
      w="full"
      alignItems="center"
      gap={{ base: '1rem', md: '3rem' }}
      justifyContent="space-between"
      zIndex={1}
      _before={{
        content: '""',
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%)',
        borderRadius: '0.25rem',
        p: '2px',
        mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
        maskComposite: 'exclude',
      }}
    >
      <Flex alignItems="center" gap="0.25rem" minW="fit-content">
        <IconTicket width="1.5rem" height="auto" />

        <Text
          fontSize={{ base: '0.875rem', md: '1.125rem' }}
          lineHeight={{ base: '1.125rem', md: '1.5rem' }}
          fontWeight="600"
          color="white.80"
        >
          Ticket Amount
        </Text>
      </Flex>
      <HStack
        background="linear-gradient(90deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 50%, rgba(255, 255, 255, 0.2) 100%)"
        borderRadius="0.25rem"
        w="full"
      >
        <Button
          {...dec}
          bg="white.10"
          color="white.95"
          isDisabled={isLoadingTickets}
          border="1.5px solid rgba(240, 240, 240, 1)"
          _hover={{ bg: 'white.20', borderColor: 'rgba(240, 240, 240, 1)' }}
          transition="all 0.2s ease-in-out"
        >
          -
        </Button>
        <Input
          textAlign="center"
          {...input}
          readOnly
          pointerEvents="none"
          border="none"
          background="transparent"
          fontSize={{ base: '1.25rem', md: '1.5rem' }}
          lineHeight="120%"
          fontWeight="600"
          color="white.95"
          {...rest}
        />
        <Button
          {...inc}
          bg="white.10"
          _hover={{ bg: 'white.20', borderColor: 'rgba(240, 240, 240, 1)' }}
          transition="all 0.2s ease-in-out"
          color="white.95"
          isDisabled={isLoadingTickets}
          border="1.5px solid rgba(240, 240, 240, 0.8)"
        >
          +
        </Button>
      </HStack>
    </Flex>
  );
};

export default TicketAmount;
